import { useNode } from "@craftjs/core";
import React from "react";
import {
  UnitInput,
  ToggleInput,
  MarginInput,
  Panel,
  Tabs,
  TextInput,
  Spacer,
  PaddingInput,
  SelectInput,
  RangeNumberInput,
} from "../../input";
import { supportLibrary } from "../../block-supports";
import { isUpdatedFromDefault } from "../../utils";
import { INPUT_LIMITS } from "../../input-limits";

export const LottieSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    fileSource,
    lottieAnimationUrl,
    ariaLabel,
    autoplay = true,
    playbackSpeed,
    loopPlayback = true,
    limitLoops,
    maxWidth,
    layoutMargin,
    layoutMarginTablet,
    layoutMarginMobile,
    layoutPadding,
    layoutPaddingTablet,
    layoutPaddingMobile,
  } = props;

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing="1rem">
          <SelectInput
            label={"File Source"}
            value={fileSource}
            onChange={(val) => {
              setProp((props) => {
                props.fileSource = val;
              });
            }}
            options={[
              { label: "Remote URL", value: "remote-url" },
              { label: "Local File", value: "local-file" },
            ]}
          />
          <TextInput
            minLength={INPUT_LIMITS["lottie-aria-label"].minlength}
            maxLength={INPUT_LIMITS["lottie-aria-label"].maxlength}
            label={"Aria Label"}
            value={ariaLabel}
            onChange={(val) => {
              setProp((props) => {
                props.ariaLabel = val;
              });
            }}
          />
          <TextInput
            minLength={INPUT_LIMITS["lottie-animation-url"].minlength}
            maxLength={INPUT_LIMITS["lottie-animation-url"].maxlength}
            resetValue={undefined}
            label={"Lottie Animation URL"}
            value={lottieAnimationUrl}
            onChange={(val) => {
              setProp((props) => {
                props.lottieAnimationUrl = val;
              });
            }}
          />
        </Spacer>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: autoplay, default: false },
            { key: playbackSpeed },
            { key: loopPlayback, default: true },
            { key: limitLoops },
          ])}
          id={`block-setting-${id}-playback-settings`}
          title={"Playback Settings"}
          initialOpen={false}
        >
          <ToggleInput
            resetValue={undefined}
            label="Autoplay"
            value={autoplay}
            onChange={(val) => {
              setProp((props) => {
                props.autoplay = val;
              });
            }}
          />

          <RangeNumberInput
            resetValue={undefined}
            label="Playback Speed"
            value={playbackSpeed}
            onChange={(val) => {
              setProp((props) => {
                props.playbackSpeed = val;
              });
            }}
          />
          <ToggleInput
            resetValue={undefined}
            label="Loop playback"
            value={loopPlayback}
            onChange={(val) => {
              setProp((props) => {
                props.loopPlayback = val;
              });
            }}
          />

          <RangeNumberInput
            resetValue={undefined}
            label="Limit Loops"
            value={limitLoops}
            onChange={(val) => {
              setProp((props) => {
                props.limitLoops = val;
              });
            }}
          />
        </Panel>
      </div>
    );
  }

  function AdvancedSettings() {
    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: layoutMargin },
            { key: layoutMarginTablet },
            { key: layoutMarginMobile },
            { key: layoutPadding },
            { key: layoutPaddingMobile },
            { key: layoutPaddingTablet },
            { key: maxWidth },
          ])}
          id={`block-setting-${id}-layout`}
          title={"Layout"}
          initialOpen={true}
        >
          <MarginInput
            resetValue={undefined}
            value={layoutMargin}
            isResponsive={true}
            valueTablet={layoutMarginTablet}
            valueMobile={layoutMarginMobile}
            onChange={(val) => {
              setProp((props) => {
                props.layoutMargin = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.layoutMarginTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.layoutMarginMobile = val;
              });
            }}
            label={"Margin"}
          />

          <PaddingInput
            resetValue={undefined}
            value={layoutPadding}
            isResponsive={true}
            valueTablet={layoutPaddingTablet}
            valueMobile={layoutPaddingMobile}
            onChange={(val) => {
              setProp((props) => {
                props.layoutPadding = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.layoutPaddingTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.layoutPaddingMobile = val;
              });
            }}
            label={"Padding"}
          />
          <UnitInput
            resetValue={undefined}
            label="Max Width"
            value={maxWidth}
            onChange={(val) => {
              setProp((props) => {
                props.maxWidth = val;
              });
            }}
          />
        </Panel>
        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: fileSource },
        { key: ariaLabel },
        { key: lottieAnimationUrl },
        { key: autoplay, default: false },
        { key: playbackSpeed },
        { key: loopPlayback, default: true },
        { key: limitLoops },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: layoutMargin },
        { key: layoutMarginTablet },
        { key: layoutMarginMobile },
        { key: layoutPadding },
        { key: layoutPaddingMobile },
        { key: layoutPaddingTablet },
        { key: maxWidth },
      ]),
    },
  ];

  return (
    <div>
      <Tabs
        id={`block-settings-tab-${id}`}
        type="block-setting"
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
